import React, { useMemo, useState } from 'react';
import { FormattedMessage, FormattedDate } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';

import config from 'appconfig';
import t from 'modules/i18n/intl';
import { Dimensions } from 'components/visuals';
import { AccountDetail } from 'modules/mac/duck/models';
import { messages } from 'modules/mac/messages';
import {
  selectIsHostedAccountProvisioning,
  selectIsOnBehalfOfEmail,
} from 'modules/mac/duck/selectors';
import { ATLASSIAN_ORGANISATION, CloudProductKeys } from 'data/products/keys';

import { StyledSpan, StyledLink } from '../StyledComponents';
import { DetailsRow } from './DetailsRow';
import PaymentDetailsSection from './licensesRowPaymentDetails';
import { StyledPipe } from '../ActionsSectionElements/StyledPipe';
import { fetchDetails } from '../../../duck/actions/accounts';
import CancelSubscriptionModal from '../../cancelSubscription/CancelSubscriptionModal';
import CancelSurveyModal from '../../cancelSubscription/CancelSurveyModal';
import { State } from '../../../../../model/State';
import { fetchIsHostedAccountProvisioning } from '../../../duck/actions/accountId';
import { AccountRenewalAction } from '../../../../../util/mac/enums';
import { removeTimezone } from 'modules/mac/common/utils';
import { checkFeatureGate, FeatureFlags } from 'util/statsig';

const getCancelActionLabel = (accountDetails: AccountDetail) => {
  if (accountDetails?.evaluation) {
    return <FormattedMessage {...messages.cancelTrial} />;
  }
  return <FormattedMessage {...messages.cancelSubscription} />;
};

const getDeactivatedAccountMessage = (accountDetails: AccountDetail) => {
  const MACCloudReadModeFG = checkFeatureGate(FeatureFlags.CART_MAC_CLOUD_READ_MODE);
  const isBB = accountDetails.merchandise.productPackage.key === CloudProductKeys.BITBUCKET;
  let message;
  if (MACCloudReadModeFG && !isBB) {
    message = t(messages.deactivatedContactUs, {
      contactLink: (chunks) => <a href={config.companyContact}>{chunks}</a>,
    });
  } else if (accountDetails?.evaluation) {
    message = t(messages.deactivatedEndedTrial, {
      convertLink: (chunks) => <a href={config.root}>{chunks}</a>,
      contactLink: (chunks) => <a href={config.companyContact}>{chunks}</a>,
    });
  } else if (accountDetails?.renewalAction === 'NONE' || accountDetails?.partnerManaged) {
    message = t(messages.deactivatedNoRenewalAction, {
      contactLink: (chunks) => <a href={config.companyContact}>{chunks}</a>,
    });
  } else {
    message = t(messages.deactivatedProvidePayment, {
      paymentLink: (chunks) => (
        <a href={`${config.root}/my/payment-details/${accountDetails.id}`}>{chunks}</a>
      ),
      br: <br />,
      contactLink: (chunks) => <a href={config.companyContact}>{chunks}</a>,
    });
  }
  return message;
};

const getAccountStatus = (accountDetails: AccountDetail) =>
  accountDetails?.renewalAction === AccountRenewalAction.NONE ? (
    <FormattedMessage {...messages.inactive} />
  ) : (
    <FormattedMessage {...messages.active} />
  );

const getEndDateLabel = (accountDetails: AccountDetail) =>
  accountDetails?.renewalAction === AccountRenewalAction.AUTO_RENEW &&
  accountDetails?.creditCardData ? (
    <FormattedMessage {...messages.renewsOn} />
  ) : (
    <FormattedMessage {...messages.expirationDate} />
  );

const CancelSubscriptionLink = ({ accountDetails }) => {
  const dispatch = useDispatch();
  const [isCancelModalOpen, setIsCancelModalOpen] = useState<boolean>(false);
  const [isSurveyModalIsOpen, setIsSurveyModalOpen] = useState<boolean>(false);

  const handleToggleCancelModal = () => {
    setIsCancelModalOpen(!isCancelModalOpen);
  };

  const handleToggleSurveyModal = () => {
    const isOpened = !isSurveyModalIsOpen;
    setIsSurveyModalOpen(isOpened);
    if (!isOpened) {
      dispatch(fetchDetails(accountDetails.id));
      dispatch(fetchIsHostedAccountProvisioning(accountDetails.id));
    }
  };

  return (
    <>
      <StyledLink data-cy="cancel-subscription-link" onClick={handleToggleCancelModal}>
        {getCancelActionLabel(accountDetails) || ''}
      </StyledLink>

      {isCancelModalOpen && (
        <CancelSubscriptionModal
          accountDetails={accountDetails}
          closeModal={handleToggleCancelModal}
          openSurveyModal={handleToggleSurveyModal}
        />
      )}
      {isSurveyModalIsOpen && (
        <CancelSurveyModal closeModal={handleToggleSurveyModal} accountDetails={accountDetails} />
      )}
    </>
  );
};

export const HostedAccountsDetails = ({ accountDetails, productKey }) => {
  const isProvisioningAcc = useSelector((state) =>
    selectIsHostedAccountProvisioning(state as State, accountDetails.id),
  );
  const isOnBehalfOfEmail: any = useSelector(selectIsOnBehalfOfEmail);

  const isCancelableSubscription = useMemo(() => {
    const productPackageKey = accountDetails?.merchandise.productPackage.key;
    const isValidProductKey =
      productPackageKey !== CloudProductKeys.BITBUCKET &&
      productPackageKey !== ATLASSIAN_ORGANISATION;
    return (
      isValidProductKey && isProvisioningAcc !== false && accountDetails?.renewalAction !== 'NONE'
    );
  }, [accountDetails, isProvisioningAcc]);

  return (
    <>
      {accountDetails?.deactivated ? (
        <DetailsRow
          label={
            accountDetails?.evaluation ? (
              <FormattedMessage {...messages.trial} />
            ) : (
              <FormattedMessage {...messages.subscription} />
            )
          }
          value={getDeactivatedAccountMessage(accountDetails)}
        />
      ) : (
        <>
          <DetailsRow
            label={
              accountDetails?.evaluation ? (
                <FormattedMessage {...messages.trial} />
              ) : (
                <FormattedMessage {...messages.subscription} />
              )
            }
            value={
              <>
                <StyledSpan data-cy="account-status" $padding={`0 ${Dimensions.units(0.5)}px 0 0`}>
                  {getAccountStatus(accountDetails)}
                </StyledSpan>
                {isCancelableSubscription && (
                  <>
                    <StyledPipe />
                    <CancelSubscriptionLink accountDetails={accountDetails} />
                  </>
                )}
              </>
            }
          />
          {!accountDetails?.extendedTrial && (
            <DetailsRow
              dataCy="end-date"
              label={getEndDateLabel(accountDetails)}
              value={
                <FormattedDate
                  value={removeTimezone(accountDetails?.latestPeriod?.endDate)}
                  day="2-digit"
                  month="long"
                  year="numeric"
                />
              }
            />
          )}
          {(!accountDetails?.partnerManaged || isOnBehalfOfEmail?.contactPartnerForAccount) &&
            accountDetails?.renewalAction === AccountRenewalAction.AUTO_RENEW && (
              <PaymentDetailsSection
                data-cy="provide-payment-details"
                accountDetails={accountDetails}
                productKey={productKey}
              />
            )}
        </>
      )}
    </>
  );
};
